import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

import { GoodsReceivedNote } from '../../core/models/inventory.models';
import { ExportService } from '../../core/services/export.service';
import { InventoryService } from '../../core/services/inventory.service';
import { SettingsService } from '../../core/services/settings.service';
import { AuthService } from '../auth/auth.service';
import { confirmAction } from '../../core/utils/confirm.util';
import {
  defaultBranchFilter,
  isBranchFilterLocked,
  resetBranchFilter,
} from '../../core/utils/branch-scope.util';
import {
  currentTimestamp,
  formatCurrency,
  formatDate,
} from '../../core/utils/format.util';
import { environment } from '../../environments/environment';
import {
  parseNonNegativeNumber,
  parsePositiveInt,
  requiredText,
} from '../../core/utils/stock-qty.util';

export type ReceiptType = Omit<GoodsReceivedNote, 'id' | 'grnNumber'> & {
  branch: string;
  location: string;
  isRentable: boolean;
  itemMode: 'existing' | 'new';
  itemId: string;
  newItemName: string;
  categoryId: string;
  unitId: string;
  quantity: number;
  unitCost: number;
};

@Component({
  selector: 'app-goods-received',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './goods-received.component.html',
  styleUrls: ['./goods-received.component.css'],
})
export class GoodsReceivedComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  private readonly auth = inject(AuthService);

  readonly timestamp = currentTimestamp;
  readonly formatCurrency = formatCurrency;
  readonly formatDate = formatDate;
  readonly suppliers = this.settings.allSuppliers;
  readonly branches = this.settings.scopedBranches;
  readonly locations = this.settings.scopedLocations;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));
  readonly categories = this.settings.allCategories;
  readonly units = this.settings.allUnits;
  readonly catalogItems = computed(() => {
    const items = this.inventory.allItems();
    if (items.length) {
      return items;
    }

    // Fallback from current stock so GRN can still pick items after stock loads.
    return this.inventory.allStockSnapshots().map((snap) => {
      const inventoryItemId =
        (snap as { inventoryItemId?: string }).inventoryItemId || snap.id;
      return {
        id: inventoryItemId,
        code: snap.itemCode,
        name: snap.itemName,
        category: snap.category,
        unit: snap.unit,
        stockLevel: snap.quantity,
        reorderLevel: 0,
        price: snap.unitValue,
        status: 'active' as const,
        branch: snap.branch,
        branchId: undefined as string | undefined,
        location: snap.location,
        locationId: undefined as string | undefined,
      };
    });
  });

  readonly modalLocations = computed(() => {
    const branchId = this.newReceipt().branch;
    if (!branchId) {
      return this.locations();
    }
    return this.locations().filter(
      (loc) => !loc.branchId || loc.branchId === branchId,
    );
  });

  readonly dropdownsReady = computed(
    () =>
      this.suppliers().length > 0 &&
      this.branches().length > 0 &&
      this.locations().length > 0 &&
      (this.catalogItems().length > 0 ||
        (this.categories().length > 0 && this.units().length > 0)),
  );
  readonly allGoodsReceived = this.inventory.allGoodsReceived;

  // ============================================
  // STATE
  // ============================================
  readonly search = signal('');
  readonly supplierFilter = signal('all');
  readonly branchFilter = signal('all');
  readonly locationFilter = signal('all');
  readonly statusFilter = signal('all');
  readonly page = signal(1);
  readonly pageSize = 10;

  readonly showReceiptModal = signal(false);
  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');
  readonly editingId = signal<string | null>(null);

  readonly isEditing = computed(() => this.editingId() !== null);

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      this.branchFilter.set(defaultBranchFilter(this.auth, branches));
    });
  }

  readonly newReceipt = signal<ReceiptType>({
    supplier: '',
    branch: '',
    location: '',
    receivedDate: new Date().toISOString().slice(0, 10),
    items: 1,
    totalValue: 0,
    status: 'draft',
    isRentable: false,
    itemMode: 'existing',
    itemId: '',
    newItemName: '',
    categoryId: '',
    unitId: '',
    quantity: 1,
    unitCost: 0,
  });

  readonly stats = computed(() => {
    const notes = this.allGoodsReceived();
    return {
      total: notes.length,
      posted: notes.filter((n) => n.status === 'posted').length,
      draft: notes.filter((n) => n.status === 'draft').length,
      cancelled: notes.filter((n) => n.status === 'cancelled').length,
    };
  });

  readonly filteredReceipts = computed(() => {
    const search = this.search().trim().toLowerCase();
    const supplier = this.supplierFilter();
    const branch = this.branchFilter();
    const location = this.locationFilter();
    const status = this.statusFilter();

    return this.allGoodsReceived().filter((note) => {
      const supplierName =
        supplier === 'all'
          ? ''
          : (this.suppliers().find((row) => row.id === supplier)?.name ??
            supplier);
      const branchName =
        branch === 'all'
          ? ''
          : (this.branches().find((row) => row.id === branch)?.name ?? branch);
      const locationName =
        location === 'all'
          ? ''
          : (this.locations().find((row) => row.id === location)?.name ??
            location);

      const matchesSearch =
        !search ||
        note.grnNumber.toLowerCase().includes(search) ||
        note.supplier.toLowerCase().includes(search) ||
        (note.itemName ?? '').toLowerCase().includes(search) ||
        (note.branch ?? '').toLowerCase().includes(search) ||
        (note.location ?? '').toLowerCase().includes(search);
      const matchesSupplier =
        supplier === 'all' || note.supplier === supplierName;
      const matchesBranch = branch === 'all' || note.branch === branchName;
      const matchesLocation =
        location === 'all' || note.location === locationName;
      const matchesStatus = status === 'all' || note.status === status;
      return (
        matchesSearch &&
        matchesSupplier &&
        matchesBranch &&
        matchesLocation &&
        matchesStatus
      );
    });
  });

  readonly paginatedReceipts = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filteredReceipts().slice(start, start + this.pageSize);
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredReceipts().length / this.pageSize)),
  );

  readonly paginationStart = computed(() =>
    this.filteredReceipts().length === 0
      ? 0
      : (this.page() - 1) * this.pageSize + 1,
  );

  readonly paginationEnd = computed(() =>
    Math.min(this.page() * this.pageSize, this.filteredReceipts().length),
  );

  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.refreshFromApi();
    }
  }

  updateNewReceipt(
    field: keyof ReceiptType,
    value: string | number | boolean,
  ): void {
    if (field === 'quantity') {
      const parsed = parsePositiveInt(value, 'Quantity');
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Enter a valid quantity',
          'error',
        );
        return;
      }
      value = parsed.value;
    }
    if (field === 'unitCost') {
      const parsed = parseNonNegativeNumber(value, 'Unit cost');
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Enter a valid unit cost',
          'error',
        );
        return;
      }
      value = parsed.value;
    }

    this.newReceipt.update((receipt) => {
      const next = { ...receipt, [field]: value };
      if (field === 'branch') {
        const stillValid = this.locations().some(
          (loc) =>
            loc.id === next.location &&
            (!loc.branchId || loc.branchId === String(value)),
        );
        if (!stillValid) {
          next.location = '';
        }
      }
      if (field === 'itemMode') {
        if (value === 'new') {
          next.itemId = '';
        } else {
          next.newItemName = '';
          next.categoryId = '';
          next.unitId = '';
        }
      }
      if (field === 'itemId') {
        const item = this.catalogItems().find(
          (row) => row.id === String(value),
        );
        if (item) {
          next.unitCost = item.price;
          next.totalValue = next.quantity * item.price;
          next.items = next.quantity;
          if (item.branch && !next.branch) {
            const branch = this.branches().find(
              (row) => row.id === item.branchId || row.name === item.branch,
            );
            next.branch = branch?.id ?? next.branch;
          }
          if (item.location && !next.location) {
            const location = this.locations().find(
              (row) => row.id === item.locationId || row.name === item.location,
            );
            next.location = location?.id ?? next.location;
          }
        }
      }
      if (field === 'quantity' || field === 'unitCost') {
        const qty = field === 'quantity' ? Number(value) : next.quantity;
        const cost = field === 'unitCost' ? Number(value) : next.unitCost;
        next.items = qty;
        next.totalValue = qty * cost;
      }
      return next;
    });
  }

  onSearchChange(): void {
    this.page.set(1);
  }

  onFilterChange(): void {
    this.page.set(1);
  }

  clearFilters(): void {
    this.search.set('');
    this.supplierFilter.set('all');
    this.branchFilter.set(resetBranchFilter(this.auth, this.branches()));
    this.locationFilter.set('all');
    this.statusFilter.set('all');
    this.page.set(1);
  }

  private showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.page.set(page);
    }
  }

  openReceiptModal(): void {
    this.editingId.set(null);
    this.newReceipt.set({
      supplier: '',
      branch: '',
      location: '',
      receivedDate: new Date().toISOString().slice(0, 10),
      items: 1,
      totalValue: 0,
      status: 'draft',
      isRentable: false,
      itemMode: 'existing',
      itemId: '',
      newItemName: '',
      categoryId: '',
      unitId: '',
      quantity: 1,
      unitCost: 0,
    });
    this.showReceiptModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  openEditModal(note: GoodsReceivedNote): void {
    this.editingId.set(note.id);

    const supplierId =
      this.suppliers().find(
        (row) => row.id === note.supplier || row.name === note.supplier,
      )?.id ?? '';
    const branchId =
      this.branches().find(
        (row) => row.id === note.branch || row.name === note.branch,
      )?.id ?? '';
    const locationId =
      this.locations().find(
        (row) => row.id === note.location || row.name === note.location,
      )?.id ?? '';

    this.newReceipt.set({
      supplier: supplierId,
      branch: branchId,
      location: locationId,
      receivedDate: note.receivedDate,
      items: note.items,
      totalValue: note.totalValue,
      status: note.status,
      isRentable: note.isRentable || false,
      itemMode: 'existing',
      itemId: '',
      newItemName: '',
      categoryId: '',
      unitId: '',
      quantity: note.items || 1,
      unitCost: note.items ? note.totalValue / note.items : 0,
    });
    this.showReceiptModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeReceiptModal(): void {
    this.showReceiptModal.set(false);
    this.editingId.set(null);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeReceiptModal();
    }
  }

  submitReceipt(status: 'draft' | 'posted' | 'cancelled' = 'posted'): void {
    const receipt = this.newReceipt();
    const supplierCheck = requiredText(receipt.supplier, 'Supplier');
    const dateCheck = requiredText(receipt.receivedDate, 'Received date');
    const branchCheck = requiredText(receipt.branch, 'Branch');
    const locationCheck = requiredText(receipt.location, 'Location');
    if (
      !supplierCheck.ok ||
      !dateCheck.ok ||
      !branchCheck.ok ||
      !locationCheck.ok
    ) {
      this.showToastMessage(
        supplierCheck.message ??
          dateCheck.message ??
          branchCheck.message ??
          locationCheck.message ??
          'Please fill in supplier, date, branch, and location',
        'error',
      );
      return;
    }

    const qtyParsed = parsePositiveInt(receipt.quantity, 'Quantity');
    if (!qtyParsed.ok) {
      this.showToastMessage(
        qtyParsed.message ?? 'Enter a quantity of at least 1',
        'error',
      );
      return;
    }
    const costParsed = parseNonNegativeNumber(receipt.unitCost, 'Unit cost');
    if (!costParsed.ok) {
      this.showToastMessage(
        costParsed.message ?? 'Unit cost cannot be negative',
        'error',
      );
      return;
    }

    if (!this.editingId()) {
      if (receipt.itemMode === 'existing' && !receipt.itemId) {
        this.showToastMessage('Select an item or switch to New item', 'error');
        return;
      }

      if (receipt.itemMode === 'new') {
        if (
          !receipt.newItemName.trim() ||
          !receipt.categoryId ||
          !receipt.unitId
        ) {
          this.showToastMessage(
            'Enter item name, category, and unit for the new item',
            'error',
          );
          return;
        }
      }
    }

    const supplier = this.suppliers().find(
      (row) => row.id === receipt.supplier,
    );
    const branch = this.branches().find((row) => row.id === receipt.branch);
    const location = this.locations().find(
      (row) => row.id === receipt.location,
    );
    const category = this.categories().find(
      (row) => row.id === receipt.categoryId,
    );
    const unit = this.units().find((row) => row.id === receipt.unitId);
    const catalogItem = this.catalogItems().find(
      (row) => row.id === receipt.itemId,
    );

    const itemDisplayName =
      receipt.itemMode === 'new'
        ? receipt.newItemName.trim()
        : (catalogItem?.name ?? '');

    const buildPayload = (inventoryItemID: number) => ({
      supplier: supplier?.name ?? receipt.supplier,
      supplierId: receipt.supplier,
      branch: branch?.name ?? (receipt.branch || ''),
      branchId: receipt.branch,
      location: location?.name ?? (receipt.location || ''),
      locationId: receipt.location,
      itemName: itemDisplayName,
      receivedDate: receipt.receivedDate,
      items: qtyParsed.value || receipt.items,
      totalValue: qtyParsed.value * costParsed.value,
      status: this.editingId() ? receipt.status : status,
      isRentable: receipt.isRentable || false,
      lines: [
        {
          inventoryItemID,
          quantity: qtyParsed.value,
          unitCost: costParsed.value,
        },
      ],
    });

    if (this.editingId()) {
      this.inventory.updateGoodsReceived(this.editingId()!, {
        supplier: supplier?.name ?? receipt.supplier,
        supplierId: receipt.supplier,
        branch: branch?.name ?? (receipt.branch || ''),
        branchId: receipt.branch,
        location: location?.name ?? (receipt.location || ''),
        locationId: receipt.location,
        receivedDate: receipt.receivedDate,
        items: receipt.quantity || receipt.items,
        totalValue: receipt.totalValue,
        status: receipt.status,
        isRentable: receipt.isRentable || false,
      } as Partial<GoodsReceivedNote>);
      this.showToastMessage('Goods received note updated', 'success');
      this.closeReceiptModal();
      return;
    }

    const createReceipt = (inventoryItemID: number) =>
      this.inventory.createGoodsReceivedViaApi(buildPayload(inventoryItemID));

    const create$ =
      receipt.itemMode === 'new'
        ? this.inventory
            .createItemViaApi({
              code: 'AUTO',
              name: receipt.newItemName.trim(),
              category: category?.name ?? receipt.categoryId,
              categoryId: receipt.categoryId,
              unit: unit?.name ?? receipt.unitId,
              unitId: receipt.unitId,
              branch: branch?.name ?? receipt.branch,
              branchId: receipt.branch,
              location: location?.name ?? receipt.location,
              locationId: receipt.location,
              // Stock is applied by the goods receipt — start at zero to avoid double-count.
              stockLevel: 0,
              reorderLevel: 0,
              price: receipt.unitCost,
              status: 'active',
              icon: 'bi-box-seam',
              isRentable: receipt.isRentable,
            })
            .pipe(
              switchMap((created) => {
                if (!created.success) {
                  return of({
                    success: false,
                    message: created.message ?? 'Failed to create new item',
                  });
                }
                if (!created.inventoryItemId || created.inventoryItemId <= 0) {
                  return of({
                    success: false,
                    message:
                      'Item was created but its ID could not be read. Refresh Item Master and receive again.',
                  });
                }
                return createReceipt(created.inventoryItemId);
              }),
            )
        : createReceipt(Number(catalogItem?.id) || 0);

    create$.subscribe((result) => {
      if (result.success) {
        this.showToastMessage(
          receipt.itemMode === 'new'
            ? 'New item created and goods received'
            : 'Goods received note created',
          'success',
        );
        this.closeReceiptModal();
      } else {
        this.showToastMessage(
          result.message ?? 'Failed to create goods receipt',
          'error',
        );
      }
    });
  }

  deleteReceipt(note: GoodsReceivedNote): void {
    if (note.status === 'cancelled') {
      this.showToastMessage('This receipt is already cancelled.', 'info');
      return;
    }

    if (
      !confirmAction(
        `Cancel receipt "${note.grnNumber}"? If it was completed, received stock will be reversed.`,
      )
    ) {
      return;
    }

    this.inventory.deleteGoodsReceivedViaApi(note.id).subscribe((result) => {
      if (result.success) {
        this.showToastMessage(`Receipt ${note.grnNumber} cancelled`, 'success');
      } else {
        this.showToastMessage(
          result.message ?? 'Failed to cancel goods receipt',
          'error',
        );
      }
    });
  }

  displayStatus(status: string): string {
    const map: Record<string, string> = {
      draft: 'Draft',
      posted: 'Completed',
      cancelled: 'Returned',
    };
    return map[status] || status;
  }

  exportData(): void {
    const rows = this.filteredReceipts().map((note) => ({
      'Receipt No.': note.grnNumber,
      Supplier: note.supplier,
      Item: note.itemName ?? '',
      Branch: note.branch,
      Location: note.location,
      Qty: note.items,
      'Total Value': note.totalValue,
      Date: note.receivedDate,
      Status: this.displayStatus(note.status),
      Rentable: note.isRentable ? 'Yes' : 'No',
    }));

    if (rows.length === 0) {
      this.showToastMessage('No receipts match the current filters.', 'info');
      return;
    }

    this.exporter.downloadCsv(
      `goods-received-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }
}
